import { Storage } from '@/utils/Storage';
import { hMacAjax as fetch } from 'grt-tool-lib-front';

const ONE_WEEK = 7 * 24 * 60 * 60; // 604800

/**
 * 通用扁平数据转树形结构
 * 数据是几级就是几级，不做层级裁剪
 * @param {Array} flatData - 扁平数组数据
 * @param {Object} options - 配置选项
 * @param {string} options.valueField - value 取值字段，默认 'id'
 * @param {string} options.labelField - label 取值字段，默认 'name'
 * @param {string} options.parentField - 父级字段名，默认 'parent_code'
 * @returns {Array} 树形结构数据
 */
export const buildTree = (flatData, options = {}) => {
    if (!Array.isArray(flatData) || flatData.length === 0) return [];

    const {
        valueField = 'id',
        labelField = 'name',
        parentField = 'parent_code'
    } = options,

     map = {},
     tree = [];

    // 初始化所有节点
    flatData.forEach(item => {
        const val = item[valueField];
        if (val !== undefined && val !== null) {
            map[val] = {
                value: val,
                label: item[labelField] || '',
                raw: item,
                children: []
            };
        }
    });

    // 构建父子关系
    flatData.forEach(item => {
        const node = map[item[valueField]];
        if (!node) return;

        const parentId = item[parentField];
        if (!parentId || parentId === '0' || parentId === null || !map[parentId]) {
            tree.push(node); // 根节点
        } else {
            map[parentId].children.push(node);
        }
    });

    return tree;
};

/**
 * 省市区县专用：扁平地区数据转树形 + 按 maxDeep 裁剪层级
 * @param {Array} flatData - 扁平的地区数据（含 code, name, parent_code, deep 字段）
 * @param {number} maxDeep - 最大层级深度（1=省, 2=省市, 3=省市区县）
 * @returns {Array} 裁剪后的树形结构
 */
export const buildAreaTree = (flatData, maxDeep = 3) => {
    if (!Array.isArray(flatData) || flatData.length === 0) return [];

    const map = {},
     tree = [];

    flatData.forEach(item => {
        map[item.code] = {
            value: item.code,
            label: item.name,
            deep: item.deep,
            parent_code: item.parent_code,
            isLeaf: item.deep >= maxDeep,
            children: []
        };
    });

    flatData.forEach(item => {
        const node = map[item.code];
        if (!node) return;

        // deep 超过最大深度的直接丢弃
        if (item.deep > maxDeep) return;

        if (item.parent_code === '0') {
            tree.push(node);
        } else {
            const parent = map[item.parent_code];
            if (parent) {
                parent.children.push(node);
            }
        }
    });

    return tree;
};

/**
 * 统一取数方法（带缓存）
 * @param {string} address - 接口地址
 * @param {string} cacheKey - 缓存 key
 * @returns {Promise<Array>} 扁平数据数组
 */
export const fetchCascaderData = async (address, cacheKey) => {
    let cached = Storage.getStorage(cacheKey);
    if (cached !== null && cached !== undefined && cached !== '') {
        return cached;
    }
    try {
        const res = await fetch.get(address),
         data = Array.isArray(res?.data) ? res.data : [];
        if (data.length > 0) {
            Storage.setStorage(cacheKey, data, ONE_WEEK);
        }
        return data;
    } catch (e) {
        console.error(`[BaseCascader] 获取级联数据失败: ${address}`, e);
        return [];
    }
};

/** 缓存 key 统一按 data_source 生成 */
export const getCacheKey = (dataSource) => `cascader_data_${dataSource}`;

export default { buildTree, buildAreaTree, fetchCascaderData, getCacheKey };
